import PropTypes from 'prop-types'
import clsx from 'clsx'

import { TicketOriginOptions, TTicketOrigin } from '../../../../shared/const'

import styles from './originBar.module.scss'

/**
 * Woher der Bestand kommt — intern erfasst oder von außen gemeldet.
 *
 * Seit dem Hilfe-Knopf stehen zwei sehr verschiedene Dinge in derselben Liste:
 * was die Verwaltung selbst notiert hat, und was eine Schülerin, eine Lehrkraft
 * oder eine Unterstützer:in geschrieben hat. Auf eine Meldung von außen wartet
 * ein Mensch auf Antwort; auf eine interne Notiz nicht. Ohne diese Leiste ist
 * der Unterschied erst in der Spalte „Erfasst von" zu sehen — zeilenweise.
 *
 * „Von Nutzer:innen" wird aufgeschlüsselt und nicht zusammengezählt: Was
 * Schüler:innen melden, geht an eine andere Stelle als das, was
 * Unterstützer:innen brauchen. Eine Summe beantwortet die Frage nicht, wegen
 * der man hinsieht.
 *
 * Jede Schaltfläche filtert die Liste darunter — dieselbe Bedienung wie im
 * Trichter darüber: nochmal klicken hebt die Einschränkung auf.
 */
export default function OriginBar({ counts, gesamt, value, onChange }) {
	const aktiv = value || []
	const intern = counts[TTicketOrigin.INTERNAL] || 0
	const vonAussen = counts[TTicketOrigin.ENDUSER] || 0

	/*
	 * Ohne eine einzige Meldung von außen wäre die Leiste eine Zeile mit dem
	 * Gesamtbestand und vier Nullen — die sagt nichts, was nicht oben steht.
	 *
	 * Entschieden wird das an `gesamt` (`enduserTotal` vom Server, gezählt ohne
	 * Suche, Status und Art) und **nicht** an den Zahlen daneben: Sonst käme
	 * und ginge die Leiste beim Filtern, je nachdem, ob der gewählte Ausschnitt
	 * gerade etwas von außen enthält. Eine 0 an einer Gruppe ist eine Auskunft;
	 * ein Bedienelement, das unter der Hand verschwindet, ist keine.
	 */
	if (!gesamt && !aktiv.length) return null

	/*
	 * Der Sammelwert und die Rollen schließen sich aus: „Von Nutzer:innen"
	 * enthält die Rollen bereits, daneben wären sie eine Teilmenge dessen, was
	 * ohnehin schon dabei ist. Wer eine Rolle wählt, meint diese Rolle.
	 */
	const schalte = wert => {
		if (aktiv.includes(wert)) return onChange(aktiv.filter(x => x !== wert))

		if (wert === TTicketOrigin.ENDUSER) {
			return onChange([...aktiv.filter(x => x === TTicketOrigin.INTERNAL), TTicketOrigin.ENDUSER])
		}

		return onChange([...aktiv.filter(x => x !== TTicketOrigin.ENDUSER), wert])
	}

	/*
	 * Eine Null ist nicht anklickbar. Der Klick führte in eine garantiert leere
	 * Liste — und wer dort landet, sucht den Fehler bei den Daten statt bei der
	 * Null, auf die er geklickt hat. Die Gruppe bleibt sichtbar: „keine" ist
	 * eine Auskunft, ein fehlender Eintrag wäre keine.
	 */
	const knopf = (wert, label, anzahl, extra) => {
		const an = aktiv.includes(wert)
		// Eine gewählte Gruppe bleibt anklickbar, auch wenn ihre Zahl auf 0
		// steht: Sonst ließe sich der eigene Filter nicht mehr aufheben.
		const leer = !anzahl && !an

		return (
			<button
				key={wert}
				type="button"
				aria-pressed={an}
				disabled={leer}
				className={clsx(styles.knopf, extra, an && styles.aktiv, leer && styles.leer)}
				title={
					leer
						? 'Dazu gibt es nichts'
						: an
						? 'Einschränkung aufheben'
						: 'Liste auf diese Herkunft einschränken'
				}
				onClick={() => schalte(wert)}
			>
				<span className={styles.zahl}>{anzahl}</span>
				<span className={styles.label}>{label}</span>
			</button>
		)
	}

	return (
		<section className={styles.root} aria-label="Herkunftsfilter">
			<div className={styles.kopf}>
				<span className={styles.titel}>Herkunft</span>
				<span className={styles.hinweis}>
					Wer den Vorgang eingebracht hat — ein Klick schränkt die Liste ein.
				</span>
			</div>

			<div className={styles.reihe}>
				{knopf(TTicketOrigin.INTERNAL, 'Intern erfasst', intern, styles.gross)}

				<span className={styles.trenner} aria-hidden="true" />

				{knopf(TTicketOrigin.ENDUSER, 'Von Nutzer:innen', vonAussen, styles.gross)}

				{/*
				 * „davon" steht ausgeschrieben da, weil die Zahlen sonst zum
				 * Zusammenzählen einladen: 7 von Nutzer:innen, 4 Schüler:innen,
				 * 3 Lehrkräfte — das sind nicht vierzehn Vorgänge, sondern
				 * sieben, zweimal gezählt.
				 */}
				<span className={styles.davon}>davon</span>

				{TicketOriginOptions.filter(option => option.value !== TTicketOrigin.OTHER || counts[option.value]).map(
					option => knopf(option.value, option.label, counts[option.value] || 0)
				)}
			</div>
		</section>
	)
}

OriginBar.propTypes = {
	// { INTERNAL: 62, ENDUSER: 18, STUDENT: 8, ... } — fehlende Gruppen zählen als 0.
	counts: PropTypes.object,
	// Ob es überhaupt Meldungen von außen gibt, ungefiltert (`enduserTotal`).
	gesamt: PropTypes.number,
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired
}

OriginBar.defaultProps = {
	counts: {},
	gesamt: 0,
	value: []
}
